import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./logincss/Login.css";
import { IonIcon } from "@ionic/react";
import { eyeOutline, eyeOffOutline } from "ionicons/icons";
import loginImg from "../../images/finalloginimage.png";
import logo from "../../images/auravit_logo.png";
import { auth, db } from "../../firebase";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, query, collection, where, getDocs } from "firebase/firestore";
import {
  clearFailedLoginAttempts,
  clearPendingAccountReactivation,
  clearStoredLoginOtpChallenge,
  createStoredLoginOtpChallenge,
  finalizePendingAccountReactivation,
  getLoginAttemptState,
  getLoginVerificationRoute,
  recordFailedLoginAttempt,
  clearStaff2FAVerified,
  maybeAutoUnlockUser,
  MAX_LOGIN_ATTEMPTS,
  setPendingAccountReactivation,
} from "../../services/authFlowService";
import { sendOtpEmail } from "../../services/otpService";
import {
  clearStaffPendingLogin,
  setStaffPendingLogin,
} from "../../services/staffPendingLoginService";
import { sessionService } from "../../services/sessionService";

const API_BASE =
  process.env.REACT_APP_FUNCTIONS_URL ||
  "https://us-central1-auravit-bb843.cloudfunctions.net";

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Clear any leftover 2FA verification state on login page load
  useEffect(() => {
    clearStaff2FAVerified();
  }, []);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    keepSignedIn: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    invalidCredentials: false,
    serverMessage: "",
    touched: {
      email: false,
      password: false,
    },
  });

  // Handle success message from password change
  useEffect(() => {
    if (location.state?.message) {
      if (location.state?.type === 'success') {
        setSuccessMessage(location.state.message);
        window.history.replaceState({}, document.title);
      } else {
        setErrors(prev => ({ ...prev, serverMessage: location.state.message }));
      }
    }
  }, [location.state]);

  const validateEmptyField = (name, value) => {
    if (name === "email") {
      if (!value.trim()) return "Email is required.";
      return "";
    }
    if (name === "password") {
      if (!value.trim()) return "Password cannot be empty.";
      return "";
    }
    return "";
  };

  const validateEmailFormat = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue = value;
    if (name === "email") newValue = value.replace(/\s/g, "").slice(0, 254);
    if (name === "password") newValue = value.replace(/\s/g, "").slice(0, 24);

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : newValue,
    }));

    if (errors.touched[name]) {
      const errorMsg = validateEmptyField(name, newValue);
      setErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
        invalidCredentials: false,
        serverMessage: "",
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    setErrors((prev) => ({
      ...prev,
      touched: { ...prev.touched, [name]: true },
      [name]: validateEmptyField(name, value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const normalizedEmail = formData.email.trim().toLowerCase();
    const emailError =
      validateEmptyField("email", normalizedEmail) ||
      (!validateEmailFormat(normalizedEmail) ? "Invalid email format." : "");
    const passwordError = validateEmptyField("password", formData.password);

    setErrors((prev) => ({
      ...prev,
      email: emailError,
      password: passwordError,
      touched: { email: true, password: true },
      invalidCredentials: false,
      serverMessage: "",
    }));

    if (emailError || passwordError) return;

    setIsSubmitting(true);
    
    // Set flag to indicate fresh login is in progress
    sessionStorage.setItem('freshLoginInProgress', 'true');

    let emailExists = false;
    let attemptState = { failedAttempts: 0, lockedUntil: null, isLocked: false, remainingMs: 0 };
    let emailQuerySnapshot = null; // Store snapshot for reuse
    
    try {
      const usersQuery = query(
        collection(db, "users"),
        where("email", "==", normalizedEmail)
      );
      const querySnapshot = await getDocs(usersQuery);
      emailQuerySnapshot = querySnapshot; // Store for later use
      emailExists = !querySnapshot.empty;
      
      if (emailExists) {
        // Pass the querySnapshot to avoid duplicate query
        attemptState = await getLoginAttemptState(normalizedEmail, db, querySnapshot);
        
        // Explicitly check if lock has expired and attempt auto-unlock
        const userData = querySnapshot.docs[0].data();
        const locked = userData.locked || false;
        const lockedUntilRaw = userData.lockedUntil || null;
        const lockedUntil = lockedUntilRaw ? 
          (typeof lockedUntilRaw === 'number' ? lockedUntilRaw : lockedUntilRaw.toMillis()) : null;
        const remainingMs = lockedUntil ? lockedUntil - Date.now() : 0;
        
        // If lock has expired, attempt auto-unlock before proceeding
        if (locked && lockedUntil && remainingMs <= 0) {
          await maybeAutoUnlockUser(normalizedEmail, db, querySnapshot);
          // Re-fetch state after auto-unlock attempt
          attemptState = await getLoginAttemptState(normalizedEmail, db, querySnapshot);
        }
      }
    } catch (checkError) {
      // Error checking email existence or lock status
    }

    if (attemptState.isLocked) {
      setErrors((prev) => ({
        ...prev,
        invalidCredentials: true,
        serverMessage: `Account locked due to too many incorrect attempts, try again in 5 minutes`,
      }));
      setIsSubmitting(false);
      return;
    }

    try {
      await setPersistence(
        auth,
        formData.keepSignedIn ? browserLocalPersistence : browserSessionPersistence
      );

      const cred = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        formData.password
      );

      await clearFailedLoginAttempts(normalizedEmail, db, emailQuerySnapshot);

      const userRef = doc(db, "users", cred.user.uid);
      const snap = await getDoc(userRef);
      const profile = snap.exists() ? snap.data() : {};

      const accountType = String(profile.accountType || "").toLowerCase();
      const emailForFlow = (cred.user.email || normalizedEmail).trim().toLowerCase();
      const isStaffAccount = accountType === "staff" || emailForFlow.endsWith("@staff.auravit.com");
      const isAdminAccount = accountType === "admin" || emailForFlow.endsWith("@admin.auravit.com");
      
      if (!isStaffAccount && !isAdminAccount && profile.verified !== true) {
        sessionStorage.removeItem('freshLoginInProgress');
        await signOut(auth);
        setErrors((prev) => ({
          ...prev,
          invalidCredentials: true,
          serverMessage: "Account not verified. Please complete registration.",
        }));
        setIsSubmitting(false);
        return;
      }

      if (isStaffAccount) {
        try {
          setStaffPendingLogin({
            email: emailForFlow,
            password: formData.password,
            keepSignedIn: formData.keepSignedIn,
            uid: cred.user.uid,
          });
        } catch {
          await signOut(auth);
          throw new Error("Could not start staff verification. Please try again.");
        }
        await signOut(auth);

        const rolesArr = Array.isArray(profile.roles)
          ? profile.roles
          : profile.role
          ? [profile.role]
          : [];
        const roleSingle = profile.role || rolesArr[0] || "";

        navigate("/staff-role-selection", {
          state: {
            email: emailForFlow,
            user: {
              uid: cred.user.uid,
              email: emailForFlow,
              accountType: "Staff",
              roles: rolesArr,
              role: roleSingle,
            },
            type: "login",
            role: roleSingle,
            roles: rolesArr,
            staffDeferredLogin: true,
            twoFactorEnabled: profile.twoFactorEnabled !== false,
          },
        });
        // Clear fresh login flag after navigation
        sessionStorage.removeItem('freshLoginInProgress');
        return;
      }

      if (profile?.status === "deactivated") {
        const firstName = profile?.personalInformation?.firstName || profile?.firstName || "";
        const lastName = profile?.personalInformation?.lastName || profile?.lastName || "";
        const userName = firstName && lastName ? `${firstName} ${lastName}` :
          (firstName || lastName || profile.username || profile.uniqueIdentifierCode || "User");

        setPendingAccountReactivation({
          uid: cred.user.uid,
          email: cred.user.email || normalizedEmail,
          userName,
          accountType: profile.accountType || "User",
        });
      } else {
        clearPendingAccountReactivation();
        // Only update lastLogin if twoFactorEnabled is false
        // If twoFactorEnabled is true, lastLogin will be updated after successful OTP verification
        if (profile.twoFactorEnabled === false) {
          await setDoc(userRef, {
            lastLogin: serverTimestamp(),
          }, { merge: true });
        }
      }

      const storedUser = {
        uid: cred.user.uid,
        email: cred.user.email,
        // firstName and lastName are no longer stored in Firestore
        // firstName: profile.firstName || "",
        // lastName: profile.lastName || "",
        username: profile.username || "",
        phone: profile.phone || "",
        contact: profile.contact || "",
        phoneLast10: profile.phoneLast10 || "",
        sex: profile.sex || "",
        dob: profile.dob || "",
        region: profile.region || "",
        province: profile.province || "",
        city: profile.city || "",
        barangay: profile.barangay || "",
                emergencyContact: profile.emergencyContact || {},
        accountType: profile.accountType || "User",
        adminId: profile.adminId || "",
        role: profile.role || "",
        roles: Array.isArray(profile.roles)
          ? profile.roles
          : profile.role
          ? [profile.role]
          : [],
        twoFactorEnabled: profile.twoFactorEnabled === undefined || profile.twoFactorEnabled === null ? true : profile.twoFactorEnabled,
      };

      localStorage.setItem("user", JSON.stringify(storedUser));

      const routeInfo = getLoginVerificationRoute(
        cred.user.email || normalizedEmail,
        profile.accountType || storedUser.accountType || ""
      );
      const emailLower = emailForFlow.toLowerCase();
      const accountTypeLower = String(profile.accountType || "").toLowerCase();
      const isStaffOtpDelivery =
        accountTypeLower === "staff" || emailLower.endsWith("@staff.auravit.com");
      const isDefaultAdmin = emailLower === "auravit@admin.auravit.com";

      const otpDeliveryTo = isDefaultAdmin ? "auravit2026@gmail.com" : emailForFlow;

      // Handle 2FA Bypass for Admin and Regular Users
      if ((accountTypeLower === "admin" && profile.twoFactorEnabled === false) || 
          (!isStaffAccount && !isAdminAccount && profile.twoFactorEnabled === false)) {
        if (profile?.status === "deactivated") {
          await finalizePendingAccountReactivation({
            uid: cred.user.uid,
            email: cred.user.email || normalizedEmail,
            userName: storedUser.username || "User",
            db,
            apiBase: API_BASE,
          });
        }
        await sessionService.trackSession(cred.user.uid);
        // Clean up old revoked sessions in the background
        sessionService.pruneOldSessions(cred.user.uid).catch(() => {});
        if (accountTypeLower === "admin") {
          navigate("/admin-management", {
            state: { user: storedUser }
          });
        } else {
          navigate("/user-home", {
            state: { user: storedUser }
          });
        }
        // Clear fresh login flag after navigation
        sessionStorage.removeItem('freshLoginInProgress');
        return;
      }



      const otpChallenge = createStoredLoginOtpChallenge({
        email: emailForFlow,
        otpDeliveryEmail: otpDeliveryTo,
        keepSignedIn: formData.keepSignedIn,
        route: routeInfo.route,
        role: storedUser.role,
        roles: storedUser.roles,
      });

      await sendOtpEmail({
        email: otpDeliveryTo,
        otp: otpChallenge.otp,
        purpose: "login",
        requestId: otpChallenge.requestId,
        expiresAt: otpChallenge.expiresAt,
      });

      navigate(routeInfo.route, {
        state: {
          email: emailForFlow,
          otpEmail: isDefaultAdmin ? "auravit2026@gmail.com" : emailForFlow,
          user: storedUser,
          type: "login",
          role: storedUser.role,
          roles: storedUser.roles,
        },
      });
    } catch (err) {
      // Handle all authentication errors including invalid credentials
      // Firebase Auth uses different error codes, so we check for both specific codes and general auth errors
      const isAuthError = 
        err?.code === "auth/invalid-credential" ||
        err?.code === "auth/wrong-password" ||
        err?.code === "auth/user-not-found" ||
        err?.code === "auth/invalid-login-credentials" ||
        err?.code === "auth/invalid-email" ||
        err?.code === "auth/user-disabled" ||
        err?.code === "auth/too-many-requests" ||
        err?.message?.includes("auth/") ||
        err?.message?.toLowerCase().includes("password") ||
        err?.message?.toLowerCase().includes("credential");

      if (isAuthError) {
        // Only track attempts if email exists
        if (emailExists) {
          try {
            const failedState = await recordFailedLoginAttempt(normalizedEmail, db, emailQuerySnapshot);
            const remainingAttempts = MAX_LOGIN_ATTEMPTS - failedState.failedAttempts;
            
            setErrors((prev) => ({
              ...prev,
              invalidCredentials: true,
              serverMessage: failedState.isLocked
                ? "Account locked due to too many incorrect attempts, try again in 5 minutes"
                : `Invalid credentials ${remainingAttempts} attempt${remainingAttempts === 1 ? '' : 's'} remaining`,
            }));
          } catch (recordError) {
            setErrors((prev) => ({
              ...prev,
              invalidCredentials: true,
              serverMessage: "Invalid credentials",
            }));
          }
        } else {
          // Email doesn't exist, don't track attempts
          setErrors((prev) => ({
            ...prev,
            invalidCredentials: true,
            serverMessage: "Invalid credentials",
          }));
        }
        sessionStorage.removeItem('freshLoginInProgress');
        setIsSubmitting(false);
        return;
      }

      clearStoredLoginOtpChallenge();
      clearStaffPendingLogin();
      try {
        await signOut(auth);
      } catch (signOutError) {
        // Login cleanup error
      }

      setErrors((prev) => ({
        ...prev,
        invalidCredentials: true,
        serverMessage: err.message || "Login failed. Please try again.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="login-logo-container">
        <img 
          src={logo} 
          alt="AuraVit" 
          className="login-logo"
          onClick={() => navigate("/")}
        />
      </div>
      <div className="login-container">
        <div className="login-card">
          <div className="login-left">
            <h1 className="login-title">Welcome Back!</h1>
            <p className="login-subtitle">
              Your gateway to trusted health knowledge and professional care.
            </p>

            <form className="login-form" onSubmit={handleSubmit}>
              <div className="input-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onKeyDown={(e) => {
                    if (e.key === " ") e.preventDefault();
                  }}
                  className={errors.email ? "input-error" : ""}
                  maxLength={254}
                />
                {errors.email && <p className="field-error">{errors.email}</p>}
              </div>

              <div className="input-group password-container">
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === " ") e.preventDefault();
                    }}
                    onBlur={handleBlur}
                    className={errors.password ? "input-error" : ""}
                    maxLength={24}
                  />
                  <button
                    type="button"
                    className="eye-icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <IonIcon icon={showPassword ? eyeOutline : eyeOffOutline} />
                  </button>
                </div>
                {errors.password && <p className="field-error">{errors.password}</p>}
              </div>

              {successMessage && (
                <p className="success-message" style={{ 
                  color: '#28a745', 
                  backgroundColor: '#d4edda', 
                  border: '1px solid #c3e6cb',
                  padding: '10px',
                  borderRadius: '5px',
                  marginBottom: '15px'
                }}>
                  {successMessage}
                </p>
              )}

              {errors.invalidCredentials && (
                <p className="invalid-message">
                  {errors.serverMessage || "Invalid Credentials"}
                </p>
              )}

              <div className="login-options">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    name="keepSignedIn"
                    checked={formData.keepSignedIn}
                    onChange={handleChange}
                  />
                  Keep me Signed In.
                </label>
                <span
                  className="forgot-password"
                  onClick={() => navigate("/forgotpass")}
                >
                  Forgot Password?
                </span>
              </div>

              <button type="submit" className="login-btn" disabled={isSubmitting}>
                {isSubmitting ? "Logging in..." : "Login"}
              </button>
            </form>

            <p className="signup-text">
              Don't have an account?{" "}
              <span
                className="signup-link"
                onClick={() => navigate("/terms-and-conditions")}
              >
                Sign Up
              </span>
            </p>
          </div>
          <div className="login-right">
            <img src={loginImg} alt="Login" className="login-panel-image" />
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
